import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { createTicket, formatFileSize, getFileIcon } from '../services/ticketService';
import { toast } from 'react-toastify';

const SubmitTicket: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    department: '',
    priority: 'medium'
  });

  const [attachments, setAttachments] = useState<FileList | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Department options
  const departments = [
    { value: 'IT', label: 'فناوری اطلاعات (IT)' },
    { value: 'reception', label: 'پذیرش' },
    { value: 'financial', label: 'مالی و حسابداری' },
    { value: 'support', label: 'جوابدهی' }
  ];

  // Priority options
  const priorities = [
    { value: 'low', label: 'کم' },
    { value: 'medium', label: 'متوسط' },
    { value: 'high', label: 'زیاد' },
    { value: 'urgent', label: 'فوری' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      // Validate file size (max 10MB per file)
      const maxSize = 10 * 1024 * 1024; // 10MB
      let hasOversizedFile = false;
      
      for (let i = 0; i < files.length; i++) {
        if (files[i].size > maxSize) {
          hasOversizedFile = true;
          break;
        }
      }
      
      if (hasOversizedFile) {
        toast.error('حداکثر حجم هر فایل باید 10 مگابایت باشد');
        return;
      }
      
      if (files.length > 5) {
        toast.error('حداکثر 5 فایل می‌توانید آپلود کنید');
        return;
      }
      
      setAttachments(files);
    }
  };

  const removeAttachment = (index: number) => {
    if (attachments) {
      const dt = new DataTransfer();
      const files = Array.from(attachments);
      
      files.forEach((file, i) => {
        if (i !== index) {
          dt.items.add(file);
        }
      });
      
      setAttachments(dt.files);
      if (fileInputRef.current) {
        fileInputRef.current.files = dt.files;
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.title.trim()) {
      newErrors.title = 'عنوان تیکت الزامی است';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'توضیحات الزامی است';
    }

    if (!formData.department) {
      newErrors.department = 'انتخاب بخش الزامی است';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const response = await createTicket(formData, attachments || undefined);
      
      toast.success('تیکت با موفقیت ایجاد شد');
      
      // Redirect to ticket list or ticket details
      navigate(`/tickets/${response.data.ticket?._id}`);
      
    } catch (error) {

      toast.error(error instanceof Error ? error.message : 'خطا در ایجاد تیکت');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">ثبت تیکت جدید</h1>
            <p className="text-gray-600">
              در این بخش می‌توانید تیکت جدید ثبت کنید و با تیم پشتیبانی در ارتباط باشید
            </p>
          </div>

          {/* Form */}
          <div className="bg-white rounded-lg shadow-md border border-gray-200">
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              
              {/* Title */}
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                  عنوان تیکت *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="عنوان مختصری از مشکل یا درخواست خود وارد کنید"
                  maxLength={200}
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                )}
              </div>

              {/* Department and Priority Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Department */}
                <div>
                  <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-2">
                    بخش مربوطه *
                  </label>
                  <select
                    id="department"
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.department ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">انتخاب کنید</option>
                    {departments.map(dept => (
                      <option key={dept.value} value={dept.value}>
                        {dept.label}
                      </option>
                    ))}
                  </select>
                  {errors.department && (
                    <p className="mt-1 text-sm text-red-600">{errors.department}</p>
                  )}
                </div>

                {/* Priority */}
                <div>
                  <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-2">
                    اولویت
                  </label>
                  <select
                    id="priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {priorities.map(priority => (
                      <option key={priority.value} value={priority.value}>
                        {priority.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  توضیحات تفصیلی *
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={6}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="لطفاً مشکل یا درخواست خود را به تفصیل شرح دهید"
                  maxLength={2000}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {formData.description.length}/2000 کاراکتر
                </p>
              </div>

              {/* File Attachments */}
              <div>
                <label htmlFor="attachments" className="block text-sm font-medium text-gray-700 mb-2">
                  فایل‌های ضمیمه
                </label>
                <div className="space-y-4">
                  <input
                    type="file"
                    id="attachments"
                    ref={fileInputRef}
                    multiple
                    onChange={handleFileChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.txt,.zip,.rar"
                  />
                  <p className="text-xs text-gray-500">
                    مجاز: تصاویر، PDF، Word، متن، ZIP (حداکثر 10 مگابایت هر فایل، حداکثر 1 فایل)
                  </p>

                  {/* Show selected files */}
                  {attachments && attachments.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-700">فایل‌های انتخاب شده:</h4>
                      {Array.from(attachments).map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                          <div className="flex items-center space-x-2 space-x-reverse">
                            <span className="text-lg">{getFileIcon(file.type)}</span>
                            <div>
                              <p className="text-sm font-medium text-gray-700">{file.name}</p>
                              <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            حذف
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-4 space-x-reverse pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => navigate('/tickets')}
                  className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  انصراف
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'در حال ارسال...' : 'ثبت تیکت'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SubmitTicket;